import {
  APP_INITIALIZER,
  ApplicationConfig,
  provideBrowserGlobalErrorListeners,
  provideZonelessChangeDetection,
  isDevMode,
} from '@angular/core';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { authInterceptor } from './core/interceptors/auth.interceptor';
import { provideAnimations } from '@angular/platform-browser/animations';
import { RouteReuseStrategy, provideRouter } from '@angular/router';
import { TranslateService, provideTranslateLoader, provideTranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';

import { AbpTranslateLoader } from './core/i18n/abp-translate.loader';
import { extractSubdomain } from './core/utils/hostname.util';
import { routes } from './app.routes';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { provideServiceWorker } from '@angular/service-worker';
import { appInfo } from './core/config/app-info';
import { environment } from '../environments/environment';

const LANGUAGE_KEY = 'prizm.wms.mobile.language';

function initApiUrl(): () => void {
  return () => {
    if (environment.apiUrl) return;
    if (!environment.apiBaseDomain) return;
    const hostname = globalThis.location?.hostname ?? '';
    const tenant = extractSubdomain(hostname, environment.appBaseDomain);
    if (tenant) {
      environment.apiUrl = `https://${tenant}.${environment.apiBaseDomain}`;
    } else {
      // Bare host, raw CF URL, localhost dev variant, or any unknown hostname:
      // use the host-level API so the tenant selector page can call IsTenantAvailable.
      environment.apiUrl = `https://${environment.apiBaseDomain}`;
    }
  };
}

function initTranslations(translate: TranslateService): () => Promise<unknown> {
  return () => {
    translate.addLangs([...appInfo.supportedLanguages]);
    translate.setDefaultLang(appInfo.defaultLanguage);
    const stored = globalThis.localStorage?.getItem(LANGUAGE_KEY);
    const lang = appInfo.supportedLanguages.includes(stored as typeof appInfo.supportedLanguages[number])
      ? (stored as typeof appInfo.supportedLanguages[number])
      : appInfo.defaultLanguage;
    return firstValueFrom(translate.use(lang));
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideBrowserGlobalErrorListeners(),
    provideZonelessChangeDetection(),
    provideHttpClient(withFetch(), withInterceptors([authInterceptor])),
    provideAnimations(),
    provideRouter(routes),
    provideIonicAngular({ mode: 'md' }),
    provideTranslateService({
      fallbackLang: appInfo.defaultLanguage,
      loader: provideTranslateLoader(AbpTranslateLoader),
    }),
    {
      provide: APP_INITIALIZER,
      useFactory: initApiUrl,
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initTranslations,
      deps: [TranslateService],
      multi: true,
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
